import React, { useEffect, useState } from "react";
import "./ProjOverView.css";
import { useDispatch, useSelector } from "react-redux";
import {
  downloadAsbuiltDocuments,
  getAsbuiltDocuments,
} from "../../actions/energyAction";
import GraphLoader from "../../components/GraphLoader";
import { download_asbuilt_document_reset } from "../../constants/dataConstants";
import DownloadLoader from "../../components/DownloadLoader";

const AsBuiltDetails = ({ databaseStr }) => {
  const { asbuiltDocument, loading, loading2, downloadAsbuiltDocument, error } =
    useSelector((state) => state.asbuiltDocument);
  const { projectDetail } = useSelector((state) => state.projectDetail);
  const [documentDetail, setDocumentDetail] = useState({
    category: "Datasheet",
    name: "Modules",
  });

  const [checkedData, setCheckedData] = useState({
    Modules: true,
    Inverter: false,
    Transformer: false,
    "DC Cables": false,
    "AC Cables": false,
    "DC SLD": false,
    "AC SLD": false,
    "String Configuration": false,
    Warranty: false,
    PVsyst: false,
    "OM Contract": false,
    reports: false,
    "Other Documents": false,
  });

  const [expandedCategories, setExpandedCategories] = useState({
    datasheet: true, // Open by default
    cables: false,
    sld: false,
    string_configuration: false,
    warranty: false,
    pvsyst: false,
    om_contract: false,
    reports: false,
    other_documents: false,
  });

  // Filter states
  const [filters, setFilters] = useState({
    year: "all",
    fileType: "all",
    searchTerm: "",
  });

  const dispatch = useDispatch();

  // Available years from documents (extracted from filenames)
  const availableYears = ["all", "2024", "2025"];
  const fileTypes = ["all", "pdf", "excel", "word"];

  // Category data with icons and subcategories
  const categories = {
    Datasheet: {
      name: "Datasheet",
      icon: "fa-file-lines",
      color: "#4CAF50",
      subcategories: [
        { id: "Modules", name: "Modules", icon: "fa-solar-panel" },
        { id: "Inverter", name: "Inverter", icon: "fa-bolt" },
        { id: "Transformer", name: "Transformer", icon: "fa-industry" },
      ],
    },
    Cables: {
      name: "Cables",
      icon: "fa-bolt",
      color: "#FF9800",
      subcategories: [
        { id: "DC Cables", name: "DC Cables", icon: "fa-plug" },
        { id: "AC Cables", name: "AC Cables", icon: "fa-plug-circle-bolt" },
      ],
    },
    SLD: {
      name: "SLD",
      icon: "fa-diagram-project",
      color: "#2196F3",
      subcategories: [
        { id: "DC SLD", name: "DC SLD", icon: "fa-diagram-successor" },
        { id: "AC SLD", name: "AC SLD", icon: "fa-diagram-predecessor" },
      ],
    },
    "String Configuration": {
      name: "String Configuration",
      icon: "fa-link",
      color: "#9C27B0",
      subcategories: [
        {
          id: "String Configuration",
          name: "String Configuration",
          icon: "fa-link",
        },
      ],
    },
    Warranty: {
      name: "Warranty",
      icon: "fa-shield-halved",
      color: "#607D8B",
      subcategories: [
        { id: "Warranty", name: "Warranty", icon: "fa-shield-halved" },
      ],
    },
    PVsyst: {
      name: "PVsyst",
      icon: "fa-solar-panel",
      color: "#009688",
      subcategories: [{ id: "PVsyst", name: "PVsyst", icon: "fa-solar-panel" }],
    },
    "OM Contract": {
      name: "O&M Contract",
      icon: "fa-handshake",
      color: "#795548",
      subcategories: [
        { id: "OM Contract", name: "O&M Contract", icon: "fa-handshake" },
      ],
    },
    ...(projectDetail?.plantDataAvailability?.pvapm_report && {
      pvapm_report: {
        name: "pvAPM Reports",
        icon: "fa-chart-line",
        color: "#E91E63",
        subcategories: [
          { id: "site_audit_report", name: "Site Audit Reports", icon: "fa-calendar-week" },
          { id: "iv_curve_report", name: "IV Curve Reports", icon: "fa-calendar-week" },
          { id: "diagnostic_report", name: "Diagnostic Reports", icon: "fa-calendar-week" },
          { id: "pvapm_weekely_report", name: "pvAPM Weekly Reports", icon: "fa-calendar-week" },
        ],
      },
    }),
    "Other Documents": {
      name: "Other Documents",
      icon: "fa-folder",
      color: "#757575",
      subcategories: [
        { id: "Other Documents", name: "Other Documents", icon: "fa-folder" },
      ],
    },
  };

  const extractYearAndMonth = (fileName) => {
    if (!fileName) return { year: "Unknown", month: "Unknown" };

    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const patterns = [
      { type: "YYMMDD", regex: /(\d{2})(\d{2})(\d{2})/ },
      { type: "YYYY-MM", regex: /(\d{4})[-_](\d{2})/ },
      { type: "YYYY", regex: /(\d{4})/ },
    ];

    for (const p of patterns) {
      const match = fileName.match(p.regex);
      if (!match) continue;

      switch (p.type) {
        case "YYMMDD": {
          const year = "20" + match[1];
          const month = months[parseInt(match[2]) - 1] || "Unknown";
          return { year, month };
        }

        case "YYYY-MM": {
          const year = match[1];
          const month = months[parseInt(match[2]) - 1] || "Unknown";
          return { year, month };
        }

        case "YYYY": {
          return { year: match[1], month: "Unknown" };
        }

        default:
          return { year: "Unknown", month: "Unknown" };
      }
    }

    return { year: "Unknown", month: "Unknown" };
  };

  // Get file type from mimeType or filename
  const getFileType = (doc) => {
    if (doc.mimeType) {
      if (doc.mimeType.includes("pdf")) return "pdf";
      if (
        doc.mimeType.includes("excel") ||
        doc.mimeType.includes("spreadsheet")
      )
        return "excel";
      if (doc.mimeType.includes("word")) return "word";
    }

    const extension = doc.originalName?.split(".").pop()?.toLowerCase();
    if (["xlsx", "xls", "csv"].includes(extension)) return "excel";
    if (["docx", "doc"].includes(extension)) return "word";
    return "pdf";
  };

  // Get file icon
  const getFileIcon = (fileType) => {
    switch (fileType) {
      case "excel":
        return "fa-file-excel";
      case "word":
        return "fa-file-word";
      default:
        return "fa-file-pdf";
    }
  };

  // Filter documents based on all active filters
  const getFilteredDocuments = () => {
    if (!asbuiltDocument || asbuiltDocument?.length === 0) return [];

    return asbuiltDocument?.sort((a, b) => {
    const nameA = (a.originalName || "").toLowerCase();
    const nameB = (b.originalName || "").toLowerCase();
    return nameB.localeCompare(nameA);
  }).filter((doc) => {
      if (filters?.searchTerm) {
        const searchLower = filters?.searchTerm?.toLowerCase();
        const fileName = doc.originalName?.toLowerCase() || "";
        const category = doc.category?.toLowerCase() || "";
        if (
          !fileName.includes(searchLower) &&
          !category.includes(searchLower)
        ) {
          return false;
        }
      }

      if (documentDetail.category === "reports" && filters?.year !== "all") {
        const { year } = extractYearAndMonth(doc.originalName);
        if (year !== filters?.year) return false;
      }

      if (filters?.fileType !== "all") {
        const docFileType = getFileType(doc);
        if (docFileType !== filters?.fileType) return false;
      }

      return true;
    });
  };

  const selectPathHandler = (category, name) => {
    if (name !== documentDetail.name || category !== documentDetail.category) {
      setFilters({
        year: "all",
        fileType: "all",
        searchTerm: "",
      });

      // dispatch(getAsbuiltDocuments(category, name, databaseStr));
      setDocumentDetail({ category, name });

      // Update checked state
      const newCheckedData = {};
      Object.keys(checkedData).forEach((key) => {
        newCheckedData[key] = key === name;
      });
      setCheckedData(newCheckedData);
    }
  };

  const toggleCategory = (categoryKey) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryKey]: !prev[categoryKey],
    }));
  };

  const downloadDocument = async (fileName, url) => {
    if (url) {
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      // dispatch(
      //   downloadAsbuiltDocuments({
      //     projectName: databaseStr,
      //     category: documentDetail.category,
      //     name: documentDetail.name,
      //     fileName,
      //     databaseStr: databaseStr,
      //   })
      // );
    }
  };

  // Filter handlers
  const handleYearChange = (year) => {
    setFilters((prev) => ({ ...prev, year }));
  };

  const handleFileTypeChange = (fileType) => {
    setFilters((prev) => ({ ...prev, fileType }));
  };

  const handleSearchChange = (searchTerm) => {
    setFilters((prev) => ({ ...prev, searchTerm }));
  };

  const clearAllFilters = () => {
    setFilters({
      year: "all",
      fileType: "all",
      searchTerm: "",
    });
  };

  const clearSearch = () => {
    setFilters((prev) => ({ ...prev, searchTerm: "" }));
  };

  const clearYearFilter = () => {
    setFilters((prev) => ({ ...prev, year: "all" }));
  };

  const clearFileTypeFilter = () => {
    setFilters((prev) => ({ ...prev, fileType: "all" }));
  };

  useEffect(() => {
    if (databaseStr) {
      dispatch(
        getAsbuiltDocuments(
          documentDetail.category,
          documentDetail.name,
          databaseStr
        )
      );
    }

    // if (downloadAsbuiltDocument) {
    //   const blob = new Blob([downloadAsbuiltDocument?.data], {
    //     type: "application/pdf",
    //   });
    //   const fileURL = window.URL.createObjectURL(blob);
    //   const link = document.createElement("a");
    //   link.href = fileURL;
    //   link.download = downloadAsbuiltDocument?.fileName || "download.pdf";
    //   document.body.appendChild(link);
    //   link.click();
    //   document.body.removeChild(link);
    //   window.URL.revokeObjectURL(fileURL);
    //   dispatch({ type: download_asbuilt_document_reset });
    // }
  }, [ dispatch, databaseStr, documentDetail]);

  const filteredDocuments = getFilteredDocuments();
  const hasActiveFilters =
    filters?.year !== "all" ||
    filters?.fileType !== "all" ||
    filters?.searchTerm !== "";
  const activeFiltersCount = [
    filters?.year !== "all",
    filters?.fileType !== "all",
    filters?.searchTerm !== "",
  ].filter(Boolean)?.length;

  return (
    <div className="asbuilt_div">
      {/* Enhanced Header */}
      <div className="asbuilt-header">
        <div className="company-name">GreenEnco</div>
        <div className="company-tagline">
          Creating A Better World In A Changing Climate
        </div>
      </div>

      {loading2 && (
        <div className="loading-overlay">
          <DownloadLoader />
        </div>
      )}

      <div className="content-area" style={{ opacity: loading2 ? 0.5 : 1 }}>
        {/* Categories Sidebar */}
        
        <div className="categories-sidebar">
          <p className="categories">Categories</p>
          <div className="categories-list">
            {Object.entries(categories)?.map(([categoryKey, category]) => (
              <div key={categoryKey} className="category-item">
                <div
                  className={`category-header ${
                    expandedCategories[categoryKey] ? "expanded" : ""
                  }`}
                  onClick={() => toggleCategory(categoryKey)}
                  style={{ borderLeftColor: category.color }}
                >
                  <div className="category-main">
                    <div className="category-icon-name">
                      <i className={`fa-solid ${category.icon}`}></i>
                      <span className="category-title">{category.name}</span>
                    </div>
                    <div className="category-arrow">
                      <i
                        className={`fa-solid fa-chevron-${
                          expandedCategories[categoryKey] ? "up" : "down"
                        }`}
                      ></i>
                    </div>
                  </div>
                  {category.subcategories?.length > 0 && (
                    <div className="subcategory-count">
                      {category.subcategories?.length} sections
                    </div>
                  )}
                </div>

                <div
                  className={`subcategories-container ${
                    expandedCategories[categoryKey] ? "expanded" : ""
                  }`}
                >
                  <div className="subcategories-list">
                    {category.subcategories?.map((subcategory) => (
                      <div
                        key={subcategory.id}
                        className={`subcategory-item ${
                          checkedData[subcategory.id] ? "active" : ""
                        }`}
                        onClick={() =>
                          selectPathHandler(categoryKey, subcategory.id)
                        }
                      >
                        <div className="subcategory-icon">
                          <i className={`fa-solid ${subcategory.icon}`}></i>
                        </div>
                        <span className="subcategory-name">
                          {subcategory.name}
                        </span>
                        {checkedData[subcategory.id] && (
                          <div className="active-indicator">
                            <i className="fa-solid fa-check"></i>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Documents Content */}
        <div className="documents-content">
          {/* Enhanced Filters Section */}
          <div className="filters-section">
            <div className="filters-header">
              <h3 className="filters-title">
                <i className="fa-solid fa-filter"></i>
                Filters
              </h3>
              {hasActiveFilters && (
                <button
                  onClick={clearAllFilters}
                  className="clear-all-filters-btn"
                >
                  <i className="fa-solid fa-broom"></i>
                  Clear All ({activeFiltersCount})
                </button>
              )}
            </div>

            <div className="filters-row">
              {/* Search Filter */}
              <div className="filter-group search-group">
                <div className="search-input-container">
                  <i className="fa-solid fa-search search-icon"></i>
                  <input
                    type="text"
                    placeholder="Search documents..."
                    value={filters?.searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="search-input"
                  />
                  {filters?.searchTerm && (
                    <button onClick={clearSearch} className="clear-search-btn">
                      <i className="fa-solid fa-times"></i>
                    </button>
                  )}
                </div>
              </div>

              {/* Year Filter (only for weekly reports) */}
              {documentDetail.category === "reports" && (
                <div className="filter-group">
                  <label className="filter-label">
                    <i className="fa-solid fa-calendar"></i>
                    Year:
                  </label>
                  <select
                    value={filters?.year}
                    onChange={(e) => handleYearChange(e.target.value)}
                    className="filter-select"
                  >
                    {availableYears?.map((year) => (
                      <option key={year} value={year}>
                        {year === "all" ? "All Years" : year}
                      </option>
                    ))}
                  </select>
                  {filters?.year !== "all" && (
                    <button
                      onClick={clearYearFilter}
                      className="clear-filter-btn"
                      title="Clear year filter"
                    >
                      <i className="fa-solid fa-times"></i>
                    </button>
                  )}
                </div>
              )}

              {/* File Type Filter */}
              <div className="filter-group">
                <label className="filter-label">
                  <i className="fa-solid fa-file"></i>
                  File Type:
                </label>
                <select
                  value={filters?.fileType}
                  onChange={(e) => handleFileTypeChange(e.target.value)}
                  className="filter-select"
                >
                  {fileTypes?.map((type) => (
                    <option key={type} value={type}>
                      {type === "all" ? "All Types" : type?.toUpperCase()}
                    </option>
                  ))}
                </select>
                {filters?.fileType !== "all" && (
                  <button
                    onClick={clearFileTypeFilter}
                    className="clear-filter-btn"
                    title="Clear file type filter"
                  >
                    <i className="fa-solid fa-times"></i>
                  </button>
                )}
              </div>
            </div>

            {/* Results Count */}
            <div className="results-info">
              <i className="fa-solid fa-file-circle-check"></i>
              Showing {filteredDocuments?.length} of{" "}
              {asbuiltDocument?.length || 0} documents
              {hasActiveFilters && " (filtered)"}
            </div>
          </div>

          {/* Documents List */}
          {loading ? (
            <div className="empty-state">
              <GraphLoader />
            </div>
          ) : filteredDocuments && filteredDocuments?.length > 0 ? (
            <div className="documents-list">
              {filteredDocuments?.map((doc, i) => {
                const fileType = getFileType(doc);
                const fileIcon = getFileIcon(fileType);
                const { year, month } = extractYearAndMonth(doc.originalName);
                return (
                  <div key={doc._id || i} className="document-card">
                    <div className="document-header">
                      <div className="document-info">
                        <div className="document-type">
                          <i className="fa-solid fa-folder"></i>
                          Category: <span>{doc.category}</span>
                        </div>
                        <div className="document-name">
                          <i className="fa-solid fa-file"></i>
                          Document:{" "}
                          <span>
                            {doc?.originalName?.length > 60
                              ? doc.originalName.substring(0, 60) + "..."
                              : doc.originalName}
                          </span>
                        </div>
                        <div className="document-meta">
                          <span className="meta-item">
                            <i className="fa-solid fa-hard-drive"></i>
                            {(doc.fileSize / 1024 / 1024).toFixed(2)} MB
                          </span>
                          {documentDetail.category === "reports" &&
                            month !== "Unknown" && (
                              <span className="meta-item">
                                <i className="fa-solid fa-calendar-days"></i>
                                {month} {year}
                              </span>
                            )}
                          <span className="meta-item">
                            <i className={`fa-solid ${fileIcon}`}></i>
                            {fileType?.toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <button
                        className={`download-btn ${
                          fileType === "pdf"
                            ? "pdf_button"
                            : fileType === "excel"
                            ? "excel_button"
                            : fileType === "word"
                            ? "word_button"
                            : ""
                        }`}
                        onClick={() =>
                          downloadDocument(doc.originalName, doc.url)
                        }
                        title={`Download ${doc.originalName}`}
                      >
                        <i className={`fa-solid ${fileIcon}`}></i>
                        Download
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state">
              <i className="fa-solid fa-file-circle-question icon"></i>
              <h1>
                {asbuiltDocument?.length > 0
                  ? "No documents match your filters"
                  : "No documents available"}
              </h1>
              <p>
                {asbuiltDocument?.length > 0
                  ? "Try adjusting your filters or search term"
                  : `No ${documentDetail.name} documents found for this category`}
              </p>
              {hasActiveFilters && (
                <button
                  onClick={clearAllFilters}
                  className="clear-filters-action-btn"
                >
                  <i className="fa-solid fa-broom"></i>
                  Clear All Filters
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AsBuiltDetails;
